"use client";
import type { Toast } from "react-hot-toast";
import toast from "react-hot-toast";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import type { ReactNode } from "react";
import { useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Buttons";
import { cn } from "../../utils/cn";

export type NotificationType = "Error" | "Warning" | "Info" | "Success";

/** Toast duration for error notifications (2 minutes) */
const ERROR_TIMEOUT_MS = 120 * 1000;
/** Default toast duration for non-error notifications (10 seconds) */
const DEFAULT_TIMEOUT_MS = 10000;

const backgroundColors: Record<NotificationType, string> = {
  Error: "bg-red-500",
  Info: "bg-blue-500",
  Warning: "bg-yellow-500",
  Success: "bg-green-500",
};
const icon: Record<NotificationType, ReactNode> = {
  Error: (
    <ExclamationCircleIcon
      aria-hidden="true"
      className="block w-8 flex-shrink-0 text-red-500"
    />
  ),
  Info: (
    <InformationCircleIcon
      aria-hidden="true"
      className="block w-8 flex-shrink-0 text-blue-500"
    />
  ),
  Warning: (
    <ExclamationCircleIcon
      aria-hidden="true"
      className="block w-8 flex-shrink-0 text-yellow-500"
    />
  ),
  Success: (
    <CheckCircleIcon
      aria-hidden="true"
      className="block w-8 flex-shrink-0 text-green-500"
    />
  ),
};

/**
 * Configuration for the notification system.
 */
export interface NotificationConfig {
  /**
   * Whether to show the "Show Error Details" button and modal.
   * Set to `false` in production to hide raw error details from users.
   * Default: `true`
   */
  showErrorDetails: boolean;
  /**
   * Called when an error notification is shown and `showErrorDetails` is `false`.
   * Use this to log the error to your monitoring service (e.g., Datadog).
   */
  onError?: (params: {
    errorId: string;
    error: unknown;
    message: string;
  }) => void;
}

let currentConfig: NotificationConfig = { showErrorDetails: true };

/**
 * Configure notification behavior globally.
 * Call this early in app initialization (e.g., in LoggingProvider).
 * Returns the previous configuration so callers can restore it on cleanup.
 */
export function configureNotifications(
  config: Partial<NotificationConfig>,
): NotificationConfig {
  const previousConfig = currentConfig;
  currentConfig = { ...currentConfig, ...config };
  return previousConfig;
}

/**
 * Configure notifications for production use with error logging.
 * Hides error details from users in production and logs them via the provided callback.
 * The callback is invoked lazily (only when an error occurs), so the logging
 * infrastructure does not need to be fully initialized at call time.
 */
export function configureProductionNotifications(
  logError: (
    message: string,
    context: { errorId: string },
    error: unknown,
  ) => void,
): void {
  configureNotifications({
    showErrorDetails: process.env.NEXT_PUBLIC_VERCEL_ENV !== "production",
    onError: ({ errorId, error, message }) => {
      logError(message, { errorId }, error);
    },
  });
}

/**
 * Generate a unique, human-readable error ID.
 * Format: ERR-XXXX-XXXX using unambiguous uppercase alphanumeric characters.
 * Excludes easily confused characters: 0, O, I, L, 1.
 */
const ERROR_ID_CHARS = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

export function generateErrorId(): string {
  const pick = () => {
    let result = "";
    for (let i = 0; i < 4; i++) {
      result +=
        ERROR_ID_CHARS[Math.floor(Math.random() * ERROR_ID_CHARS.length)];
    }
    return result;
  };
  return `ERR-${pick()}-${pick()}`;
}

export const notify = (
  type: NotificationType,
  message: string,
  { error, timeout }: { error?: unknown; timeout?: number } = {},
) => {
  // Dismiss all existing toasts before showing new one
  toast.dismiss();

  let errorId: string | undefined;
  let displayError = error;

  if (error && !currentConfig.showErrorDetails) {
    errorId = generateErrorId();
    displayError = undefined;
    try {
      currentConfig.onError?.({ errorId, error, message });
    } catch {
      // Don't let a logging failure prevent the notification from showing
    }
  }

  const getDuration = () => {
    if (timeout !== undefined) return timeout;
    if (error || type === "Error") return ERROR_TIMEOUT_MS;
    return DEFAULT_TIMEOUT_MS;
  };

  toast.custom(
    (t) => (
      <NotificationToast
        error={displayError}
        errorId={errorId}
        message={message}
        toast={t}
        type={type}
      />
    ),
    { duration: getDuration() },
  );
};

function NotificationToast({
  toast: toastProp,
  type,
  message,
  error,
  errorId,
}: {
  toast: Toast;
  type: NotificationType;
  message: string;
  error?: unknown;
  errorId?: string;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyText = navigator.clipboard.writeText
    ? async (text: string) => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      }
    : null;

  function showErrorDetails() {
    setShowDialog(true);
  }

  const formatError = (err: unknown): string => {
    if (!err) return "";
    try {
      if (err instanceof Error) {
        // Include standard Error properties plus any additional enumerable
        // properties (e.g., Axios .response, .config, .code, .cause, etc.)
        return JSON.stringify(
          {
            name: err.name,
            message: err.message,
            stack: err.stack,
            ...(err as unknown as Record<string, unknown>),
          },
          null,
          2,
        );
      }
      if (typeof err === "object") {
        return JSON.stringify(err, null, 2);
      }
      return String(err);
    } catch {
      return String(err);
    }
  };

  const errorText = error ? formatError(error) : "";

  function closeDialog() {
    toast.dismiss(toastProp.id);
    setShowDialog(false);
  }
  if (currentConfig.showErrorDetails && showDialog) {
    return (
      <>
        <Modal
          data-testid="notification-error-modal"
          isOpen={showDialog}
          onClose={closeDialog}
        >
          <Modal.Header className="flex items-center gap-5">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="h-10 w-10 text-red-500"
            />
            Error Details{" "}
          </Modal.Header>
          <Modal.Content>
            <pre data-testid="notification-error-details" className="text-sm">
              {errorText}
            </pre>
          </Modal.Content>
          <Modal.Footer className="flex justify-end gap-5">
            {copyText ? (
              <Button
                data-testid="notification-error-copy-button"
                onClick={() => copyText(errorText)}
                vColor={copied ? "success" : "secondary"}
              >
                {copied ? "Copied to Clipboard" : "Copy to Clipboard"}
              </Button>
            ) : null}
            <Button
              data-testid="notification-error-modal-close"
              className="px-4"
              onClick={closeDialog}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
  return (
    <div
      data-testid={`notification-${type.toLowerCase()}`}
      role={type === "Error" ? "alert" : "status"}
      aria-live={type === "Error" ? "assertive" : "polite"}
      aria-atomic="true"
      className={cn(
        "relative w-full flex-col overflow-hidden rounded-md bg-white text-gray-700 drop-shadow-lg sm:w-min sm:flex-row",
        "flex",
        toastProp.visible ? "animate-enter" : "animate-leave",
      )}
    >
      <div className="absolute inset-0 rounded-md border border-gray-400" />
      <div
        className={cn(
          "absolute top-0 bottom-0 left-0 z-10 h-full w-3",
          backgroundColors[type],
        )}
      />
      <div className="relative ml-2 flex flex-shrink flex-grow gap-3 py-4 pl-3">
        {icon[type]}
        <span className="mr-1 max-w-full min-w-[10rem] sm:w-min sm:max-w-[15rem] sm:min-w-[20rem]">
          <span className="sr-only">{type} notification:</span>
          <span className="mr-1 font-bold">{type}!</span>{" "}
          <div data-testid="notification-message" className="break-words">
            {message}
          </div>
          {currentConfig.showErrorDetails && error ? (
            <button
              data-testid="notification-error-details-button"
              aria-label="Show detailed error information"
              className="mt-2 text-sm text-blue-500 underline"
              onClick={showErrorDetails}
            >
              Show Error Details
            </button>
          ) : null}
          {errorId ? (
            <p
              data-testid="notification-error-id"
              className="mt-2 text-xs text-gray-500"
            >
              Reference: <code className="font-mono select-all">{errorId}</code>
            </p>
          ) : null}
        </span>
      </div>
      <button
        data-testid="notification-close-button"
        aria-label={`Dismiss ${type.toLowerCase()} notification`}
        className="z-10 mr-2 ml-4 block flex-shrink-0 border-gray-300 py-5 pr-2 align-middle sm:mr-0 sm:ml-1 sm:w-24 sm:border-l sm:px-3"
        onClick={() => toast.dismiss(toastProp.id)}
      >
        Close
      </button>
    </div>
  );
}
